import SubscriptionLimitReached from "@shared/components/SubscriptionLimitReached";
import theme from "@shared/utils/themeColors";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AuthenticatedLayout from "../../../shared/layouts/AuthenticatedLayout";
import TestPassage from "../components/TestPassage";
import TestQuestion from "../components/TestQuestion";
import TestResults from "../components/TestResults";
import { TestDetailService } from "../services/test-detail.service";
import { TestResult } from "../types/test-detail.types";

export default function TestDetail() {
  const { testId } = useParams<{ testId: string }>();
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [highlightedLines, setHighlightedLines] = useState<
    | {
        start?: number;
        end?: number;
      }
    | undefined
  >();
  const [highlightedQuestionNumber, setHighlightedQuestionNumber] = useState<
    number | undefined
  >();
  const [showResumeDialog, setShowResumeDialog] = useState(false);
  const [testResults, setTestResults] = useState<TestResult>();
  const [subscriptionLimitReached, setSubscriptionLimitReached] =
    useState(false);
  const passagesContainerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const {
    data: test,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["test", testId],
    queryFn: async () => {
      try {
        return await TestDetailService.getTestDetail(testId!);
      } catch (error: any) {
        if (error.name === "SUBSCRIPTION_LIMIT_REACHED") {
          setSubscriptionLimitReached(true);
        }
        throw error;
      }
    },
    enabled: !!testId,
    retry: false,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchOnReconnect: true,
    gcTime: 0,
  });

  // Track if we've initialized the test state
  const [isInitialized, setIsInitialized] = useState(false);

  // Find question indices by ID
  const findQuestionIndices = (questionId: string) => {
    if (!test) return null;

    for (
      let sectionIndex = 0;
      sectionIndex < test.sections.length;
      sectionIndex++
    ) {
      const section = test.sections[sectionIndex];
      const questionIndex = section.questions.findIndex(
        (q) => q.id === questionId
      );
      if (questionIndex !== -1) {
        return { sectionIndex, questionIndex };
      }
    }
    return null;
  };

  // Initialize test state and show resume dialog if needed
  useEffect(() => {
    if (!test || isInitialized) return;

    const hasProgress =
      test.currentProgress?.answers?.length > 0 &&
      test.currentProgress?.lastQuestionId;
    if (hasProgress) {
      const indices = findQuestionIndices(test.currentProgress?.lastQuestionId);

      if (indices) {
        const nextQuestionIndex = indices.questionIndex + 1;

        // If nextQuestionIndex is last and lastSectionIndex is last
        if (
          indices.questionIndex ===
            test.sections[indices.sectionIndex].questions.length - 1 &&
          indices.sectionIndex === test.sections.length - 1
        ) {
          setCurrentSectionIndex(indices.sectionIndex);
          setCurrentQuestionIndex(indices.questionIndex);
        } else if (
          nextQuestionIndex <
          test.sections[indices.sectionIndex].questions.length
        ) {
          setCurrentSectionIndex(indices.sectionIndex);
          setCurrentQuestionIndex(nextQuestionIndex);
        } else if (indices.sectionIndex + 1 < test.sections.length) {
          setCurrentSectionIndex(indices.sectionIndex + 1);
          setCurrentQuestionIndex(0);
        }
      }

      // Show resume dialog only on initial load
      setShowResumeDialog(true);
    }

    setIsInitialized(true);
  }, [test]);

  const queryClient = useQueryClient();

  const { mutateAsync: restartTest, isPending: isRestarting } = useMutation({
    mutationFn: (testId: string) => TestDetailService.restartTest(testId),
    onSuccess: async (_, testId) => {
      await queryClient.invalidateQueries({ queryKey: ["test", testId] });
      setCurrentSectionIndex(0);
      setCurrentQuestionIndex(0);
    },
  });

  const handleResumeTest = async (resume: boolean) => {
    try {
      setShowResumeDialog(false);

      if (!test) return;

      if (resume && test.currentProgress?.answers.length > 0) {
      } else {
        // Restart test
        await restartTest(test.id);
      }
    } catch (error) {
      setCurrentSectionIndex(0);
      setCurrentQuestionIndex(0);
    }
  };

  const { mutateAsync: completeTest, isPending: isCompleting } = useMutation({
    mutationFn: (testId: string) => TestDetailService.completeTest(testId),
    onSuccess: (result) => {
      setTestResults(result);
    },
  });

  const handleNext = async () => {
    if (!currentSection || !test) return;

    const isLastQuestion =
      currentQuestionIndex === currentSection.questions.length - 1 &&
      currentSectionIndex === test.sections.length - 1;

    if (isLastQuestion) {
      return;
    }

    if (currentQuestionIndex < currentSection.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else if (currentSectionIndex < test.sections.length - 1) {
      if (passagesContainerRef.current) {
        passagesContainerRef.current.scrollTop = 0;
      }
      setCurrentSectionIndex(currentSectionIndex + 1);
      setCurrentQuestionIndex(0);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    } else if (currentSectionIndex > 0) {
      setCurrentSectionIndex(currentSectionIndex - 1);
      setCurrentQuestionIndex(
        test!.sections[currentSectionIndex - 1].questions.length - 1
      );
    }
  };

  const handleHoverLineReference = (
    start?: number,
    end?: number,
    questionNumber?: number
  ) => {
    setHighlightedLines(start !== undefined ? { start, end } : undefined);
    setHighlightedQuestionNumber(questionNumber);
  };

  // Function to convert newlines to <br> elements
  const formatWithLineBreaks = (text: string) => {
    if (!text) return null;

    return text.split("\n").map((line, index, array) => (
      <React.Fragment key={index}>
        {line}
        {index < array.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  const currentSection = test?.sections[currentSectionIndex];
  const currentQuestion = currentSection?.questions[currentQuestionIndex];
  const totalQuestions =
    test?.sections.reduce(
      (total, section) => total + section.questions.length,
      0
    ) || 0;

  // Get the selected option for the current question
  const getCurrentQuestionAnswer = () => {
    if (!currentQuestion) return undefined;

    // First check if we have a locally updated correctAnswer
    if (currentQuestion.correctAnswer) {
      return currentQuestion.correctAnswer;
    }

    // Then check saved answers
    if (test?.currentProgress?.answers) {
      const answer = test.currentProgress.answers.find(
        (a) => a.questionId === currentQuestion.id
      );
      return answer?.selectedOption;
    }

    return undefined;
  };

  const { mutateAsync: saveAnswer } = useMutation({
    mutationFn: (params: {
      testId: string;
      questionId: string;
      selectedOption: string;
    }) =>
      TestDetailService.saveAnswer(params.testId, {
        questionId: params.questionId,
        selectedOption: params.selectedOption,
      }),
    onMutate: async (params) => {
      if (!test) return;

      const updatedProgress = {
        ...test.currentProgress,
        currentQuestionIndex: currentQuestionIndex + 1,
        answers: [
          ...(test.currentProgress?.answers || []),
          {
            questionId: params.questionId,
            selectedOption: params.selectedOption,
            answeredAt: new Date(),
          },
        ],
      };

      // Update local cache immediately
      queryClient.setQueryData(["test", params.testId], {
        ...test,
        currentProgress: updatedProgress,
      });
    },
    onError: (error) => {
      if (testId) {
        // Invalidate the query to fetch fresh data
        queryClient.invalidateQueries({ queryKey: ["test", testId] });
        console.error("Error saving answer:", error);
      }
    },
  });

  const handleAnswer = async (optionId: string) => {
    if (!testId || !currentQuestion) return;

    // Save answer to database
    await saveAnswer({
      testId,
      questionId: currentQuestion.id,
      selectedOption: optionId,
    });

    // Update the selected option in the current question
    if (test && currentSection && currentQuestion) {
      // Create a deep copy of the test object
      const updatedTest = { ...test };

      // Update the correct answer in the current question
      updatedTest.sections[currentSectionIndex].questions[
        currentQuestionIndex
      ].correctAnswer = optionId;

      // Update the local cache
      queryClient.setQueryData(["test", testId], updatedTest);
    }

    const isLastQuestion =
      currentQuestionIndex === currentSection.questions.length - 1 &&
      currentSectionIndex === test.sections.length - 1;

    if (isLastQuestion) {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      await completeTest(testId);
    }
    await handleNext();
  };

  if (testResults || test?.result) {
    const results = testResults || test!.result;
    return (
      <AuthenticatedLayout defaultCollapsed={true}>
        <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
          <TestResults
            sections={test!.sections}
            answers={results.answers}
            score={results.score}
            correctAnswers={results.correctAnswers}
            incorrectAnswers={results.incorrectAnswers}
            onRestart={() => restartTest(testId!)}
            isRestarting={isRestarting}
            testProgressId={test.currentProgress?.id}
          />
        </div>
      </AuthenticatedLayout>
    );
  }

  if (isLoading || isRestarting) {
    return (
      <AuthenticatedLayout defaultCollapsed={true}>
        <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="h-96 bg-gray-200 rounded"></div>
              <div className="h-96 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </AuthenticatedLayout>
    );
  }

  if (subscriptionLimitReached) {
    return (
      <AuthenticatedLayout defaultCollapsed={true}>
        <SubscriptionLimitReached
          resourceType="tests"
          returnPath="/tests"
          returnLabel="Return to Tests"
        />
      </AuthenticatedLayout>
    );
  }

  if (!test) {
    return (
      <AuthenticatedLayout defaultCollapsed={true}>
        <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Test Not Found</h2>
            <p className={`mt-2 ${theme.text.muted}`}>
              The test you are looking for does not exist or you don't have
              access to it.
            </p>
            <button
              onClick={() => navigate("/tests")}
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-violet-600 hover:bg-violet-700"
            >
              Back to List
            </button>
          </div>
        </div>
      </AuthenticatedLayout>
    );
  }

  return (
    <AuthenticatedLayout defaultCollapsed={true}>
      <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold">
              {test.name}
            </h1>
          </div>
          {currentSection?.header?.title && (
            <>
              <h2 className="text-xl text-slate-600 dark:text-slate-400 mt-2">
                {currentSection.header.title}
              </h2>
              {currentSection.header.description && (
                <p className="text-slate-500 dark:text-slate-400 mt-1">
                  {formatWithLineBreaks(currentSection.header.description)}
                </p>
              )}
            </>
          )}
        </div>

        {/* Content */}
        <div className="grid lg:grid-cols-2 gap-6 pb-16 lg:pb-0 lg:h-[calc(100vh-16rem)]">
          {/* Left: Passages */}
          <div className="overflow-y-auto pr-4" ref={passagesContainerRef}>
            {/* Directions */}
            {currentSection?.directions && (
              <div className={`${theme.bg.primary} rounded-lg shadow p-6 mb-6`}>
                <div className="prose max-w-none">
                  <h3 className="text-lg font-semibold mb-2">Directions</h3>
                  <div className="text-slate-600 dark:text-slate-300">
                    {formatWithLineBreaks(currentSection.directions)}
                  </div>
                </div>
              </div>
            )}

            {/* Introduction */}
            {currentSection?.introduction?.title && (
              <div className={`${theme.bg.primary} rounded-lg shadow p-6 mb-6`}>
                <div className="prose max-w-none">
                  <h3 className="text-lg font-semibold mb-2">
                    {currentSection.introduction.title}
                  </h3>
                  {currentSection.introduction.description && (
                    <div className="text-slate-600 dark:text-slate-300">
                      {formatWithLineBreaks(
                        currentSection.introduction.description
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
            {currentSection?.passages.map((passage) => (
              <TestPassage
                key={passage.id}
                passage={passage}
                className="mb-6"
                highlightedLines={highlightedLines}
                highlightedQuestionNumber={highlightedQuestionNumber}
              />
            ))}

            {/* Iframe for Math sections */}
            {currentSection &&
              [3, 4, 6].includes(currentSection.sectionNumber) && (
                <div className="w-full responsive-iframe-container mt-0">
                  <iframe
                    src="https://www.desmos.com/calculator"
                    style={{ border: "0px #ffffff none" }}
                    name="myiFrame"
                    scrolling="no"
                    frameBorder="1"
                    height="500px"
                    width="800px"
                    allowFullScreen
                  />
                </div>
              )}
          </div>

          {/* Right: Questions */}
          <div className="overflow-y-auto">
            {currentQuestion && (
              <TestQuestion
                question={currentQuestion}
                selectedOption={getCurrentQuestionAnswer()}
                onAnswer={handleAnswer}
                onHoverLineReference={handleHoverLineReference}
                hasPrevious={
                  currentQuestionIndex > 0 || currentSectionIndex > 0
                }
                onPrevious={handlePrevious}
                onNext={handleNext}
                isLast={
                  currentQuestionIndex ===
                    currentSection.questions.length - 1 &&
                  currentSectionIndex === test.sections.length - 1
                }
              />
            )}
          </div>
        </div>
      </div>

      {/* Resume Dialog */}
      {showResumeDialog &&
        test.currentProgress &&
        test.currentProgress.answers.length > 0 && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div
              className={`${theme.bg.primary} rounded-lg p-6 max-w-md w-full`}
            >
              <h3 className="text-lg font-semibold mb-4">Test in Progress</h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                You have completed {test.currentProgress.answers.length} out of{" "}
                {totalQuestions} questions. Would you like to continue where you
                left off or start over?
              </p>
              <div className="flex gap-4">
                <button
                  onClick={() => handleResumeTest(true)}
                  className={`flex-1 ${theme.button.primary}`}
                >
                  Continue
                </button>
                <button
                  onClick={() => handleResumeTest(false)}
                  className={`flex-1 btn ${theme.button.tertiary}`}
                >
                  Start Over
                </button>
              </div>
            </div>
          </div>
        )}
    </AuthenticatedLayout>
  );
}
